import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

interface AccessibilityContextType {
  highContrast: boolean;
  toggleHighContrast: () => void;
  reducedMotion: boolean;
  toggleReducedMotion: () => void;
  fontSize: "normal" | "large" | "extra-large";
  setFontSize: (size: "normal" | "large" | "extra-large") => void;
  focusVisible: boolean;
  announceMessage: (message: string) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

const STORAGE_KEY = "sentinela_accessibility_settings";

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [fontSize, setFontSizeState] = useState<"normal" | "large" | "extra-large">("normal");
  const [focusVisible, setFocusVisible] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  // Load settings from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const settings = JSON.parse(stored);
        setHighContrast(settings.highContrast ?? false);
        setReducedMotion(settings.reducedMotion ?? false);
        setFontSizeState(settings.fontSize ?? "normal");
      } catch {
        // Invalid JSON, ignore
      }
    }

    // Check system preference for reduced motion
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches) {
      setReducedMotion(true);
    }

    // Detect keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        setFocusVisible(true);
      }
    };

    const handleMouseDown = () => {
      setFocusVisible(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      highContrast,
      reducedMotion,
      fontSize
    }));
  }, [highContrast, reducedMotion, fontSize]);

  // Apply high contrast mode
  useEffect(() => {
    if (highContrast) {
      document.documentElement.classList.add("high-contrast");
    } else {
      document.documentElement.classList.remove("high-contrast");
    }
  }, [highContrast]);

  // Apply reduced motion
  useEffect(() => {
    if (reducedMotion) {
      document.documentElement.classList.add("reduce-motion");
    } else {
      document.documentElement.classList.remove("reduce-motion");
    }
  }, [reducedMotion]);

  // Apply font size
  useEffect(() => {
    document.documentElement.classList.remove("font-large", "font-extra-large");
    if (fontSize === "large") {
      document.documentElement.classList.add("font-large");
    } else if (fontSize === "extra-large") {
      document.documentElement.classList.add("font-extra-large");
    }
  }, [fontSize]);

  // Apply focus visible class
  useEffect(() => {
    if (focusVisible) {
      document.documentElement.classList.add("focus-visible-enabled");
    } else {
      document.documentElement.classList.remove("focus-visible-enabled");
    }
  }, [focusVisible]);

  const toggleHighContrast = useCallback(() => {
    setHighContrast(prev => !prev);
  }, []);

  const toggleReducedMotion = useCallback(() => {
    setReducedMotion(prev => !prev);
  }, []);

  const setFontSize = useCallback((size: "normal" | "large" | "extra-large") => {
    setFontSizeState(size);
  }, []);

  const announceMessage = useCallback((message: string) => {
    setAnnouncement(message);
    // Clear after screen reader has time to read
    setTimeout(() => setAnnouncement(""), 1000);
  }, []);

  return (
    <AccessibilityContext.Provider
      value={{
        highContrast,
        toggleHighContrast,
        reducedMotion,
        toggleReducedMotion,
        fontSize,
        setFontSize,
        focusVisible,
        announceMessage
      }}
    >
      {children}
      
      {/* Live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error("useAccessibility must be used within AccessibilityProvider");
  }
  return context;
}
